import { useState } from "react";
import { useAuthStore } from "../lib/authStore";
import ssnLogo from "../images/ssn-logo.png";

const LINKS = ["Overview", "About", "Strategy", "How It Works", "Entry Valuation", "Exit & Returns", "Benefits", "Leadership" ,"Investors"];

interface NavbarProps {
  activeSection: string;
  onNav: (section: string) => void;
}

export default function Navbar({ activeSection, onNav }: NavbarProps) {
  const { user, logout } = useAuthStore();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const closeDrawer = () => setDrawerOpen(false);

  return (
    <>
      <style>{`
        /* Desktop Configuration: Fixed Left Sidebar Panel Layout */
        .ssn-navbar {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: 260px;
          height: 100vh;
          border-right: 1px solid rgba(255,255,255,0.06);
          border-bottom: none;
        }
        .ssn-nav-inner {
          flex-direction: column;
          align-items: stretch;
          padding: 32px 20px;
          gap: 32px;
        }
        .ssn-desktop-links {
          display: flex !important;
          flex-direction: column;
          gap: 6px;
        }
        .ssn-burger { display: none !important; }

        /* Responsive Breakpoint: Mobile & iPad Viewports */
        @media (max-width: 1024px) {
          .ssn-navbar {
            width: 100%;
            height: 64px;
            bottom: auto;
            border-right: none;
            border-bottom: 1px solid rgba(255,255,255,0.06);
          }
          .ssn-nav-inner {
            flex-direction: row;
            align-items: center;
            padding: 0 20px;
            height: 100%;
            gap: 16px;
          }
          .ssn-desktop-links { display: none !important; }
          .ssn-burger { display: flex !important; align-items: center; }
        }
      `}</style>
      
      <nav className="ssn-navbar" style={S.nav}>
        <div className="ssn-nav-inner" style={S.inner}>
          
          {/* Top/Header Branding Section */}
          <div style={S.headerGroup}>
            {/* Hamburger Menu — Left side on mobile views */}
            <button
              className="ssn-burger"
              style={S.burger}
              onClick={() => setDrawerOpen(!drawerOpen)}
              aria-label="Menu"
            >
              <span style={S.burgerIcon}>☰</span>
            </button>

            {/* Logo Unit */}
            <div style={S.logo}>
              <div style={S.topbarLogo}>
                <img src={ssnLogo} alt="SSN" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
              </div>
              <div>
                <div style={S.logoName}>ShivSena Networks, LLC</div>
                <div style={S.logoSub}>PRIVATE EQUITY FUND</div>
              </div>
            </div>
          </div>

          {/* Desktop Left-Side Vertical Links Panel */}
          <div className="ssn-desktop-links" style={S.desktopLinks}>
            {LINKS.map((l) => (
              <button
                key={l}
                onClick={() => onNav(l)}
                style={{
                  ...S.link,
                  color: activeSection === l ? "var(--teal-light)" : "rgba(240,237,232,0.65)",
                  background: activeSection === l ? "rgba(29,158,117,0.07)" : "transparent",
                }}
              >
                {activeSection === l && <div style={S.activeLine} />}
                {l}
              </button>
            ))}
          </div>

          {/* Flexible Spacer for desktop vertical stacking pushing profile to the bottom */}
          <div style={{ flex: 1 }} />

          {/* Portal User Row / CTA Action */}
          <div style={S.right}>
            {user ? (
              <div style={S.userRow}>
                <img src={user.picture} alt="" style={S.avatar} />
                <div style={S.userInfo}>
                  <span style={S.userName}>{user.name.split(" ")[0]}</span>
                  <button onClick={logout} style={S.logoutBtn}>Sign out</button>
                </div>
              </div>
            ) : (
              <button onClick={() => onNav("login")} style={S.portalBtn}>
                Portal Login
              </button>
            )}
          </div>
        </div>
      </nav>

      {/* Mobile Drawer Backdrop overlay */}
      {drawerOpen && (
        <div style={S.backdrop} onClick={closeDrawer} />
      )}

      {/* Mobile Responsive Left Slide-Out Drawer Panel */}
      <div style={{
        ...S.drawer,
        transform: drawerOpen ? "translateX(0)" : "translateX(-100%)",
      }}>
        <div style={S.drawerHeader}>
          <div style={S.drawerLogo}>
            <div style={S.topbarLogo}>
              <img src={ssnLogo} alt="SSN" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            </div>
            <div>
              <div style={S.logoName}>ShivSena Networks, LLC</div>
              <div style={S.logoSub}>PRIVATE EQUITY Fund</div>
            </div>
          </div>
        </div>

        <div style={S.drawerLinks}>
          {LINKS.map((l) => (
            <button
              key={l}
              onClick={() => { onNav(l); closeDrawer(); }}
              style={{
                ...S.drawerLink,
                ...(activeSection === l ? S.drawerLinkActive : {}),
              }}
            >
              {activeSection === l && <div style={S.drawerActivePill} />}
              {l}
            </button>
          ))}
        </div>

        {!user ? (
          <button
            onClick={() => { onNav("login"); closeDrawer(); }}
            style={S.drawerPortalBtn}
          >
            Portal Login
          </button>
        ) : (
          <div style={S.drawerUserRow}>
            <img src={user.picture} alt="" style={S.avatar} />
            <span style={{ ...S.userName, fontSize: 13 }}>{user.name.split(" ")[0]}</span>
            <button onClick={() => { logout(); closeDrawer(); }} style={S.logoutBtn}>Sign out</button>
          </div>
        )}
      </div>
    </>
  );
}

const S: Record<string, React.CSSProperties> = {
  /* ── Sidebar Container base styles ── */
  nav: {
    zIndex: 200,
    background: "rgba(10,15,14,0.92)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
  },
  inner: {
    height: "100%",
    display: "flex",
  },
  headerGroup: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },

  /* ── Brand Logo layouts ── */
  logo: { display: "flex", alignItems: "center", gap: 10, flexShrink: 0 },
  topbarLogo: {
    width: 38, height: 38, borderRadius: 7, flexShrink: 0,
    overflow: "hidden", background: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 1,
  },
  logoName: { fontSize: 13, fontWeight: 600, color: "var(--teal-light)", letterSpacing: "0.01em" },
  logoSub: { fontSize: 9, letterSpacing: "0.15em", color: "var(--text-muted)", marginTop: 1 },

  /* ── Responsive Burger Button ── */
  burger: {
    background: "none", border: "none", cursor: "pointer",
    padding: "4px 6px", flexShrink: 0,
  },
  burgerIcon: { fontSize: 22, color: "var(--text-primary)", lineHeight: 1 },

  /* ── Left Sidebar Vertical Desktop Links ── */
  desktopLinks: {
    width: "100%",
  },
  link: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    padding: "11px 16px", 
    fontSize: 13, 
    fontWeight: 500,
    letterSpacing: "0.01em", 
    border: "none",
    borderRadius: 6,
    cursor: "pointer", 
    transition: "all 0.15s ease", 
    whiteSpace: "nowrap",
    position: "relative",
    textAlign: "left",
  },
  activeLine: {
    position: "absolute", left: 0, top: "50%",
    transform: "translateY(-50%)",
    width: 3, height: 18,
    background: "var(--teal-light)", borderRadius: "0 2px 2px 0",
  },

  /* ── Base User Profile & Lower Layout panel actions ── */
  right: { display: "flex", alignItems: "center", flexShrink: 0 },
  portalBtn: {
    width: "100%",
    padding: "10px 16px", borderRadius: 6,
    border: "1px solid var(--teal-dim)",
    color: "var(--teal-light)", fontSize: 12, fontWeight: 500,
    cursor: "pointer", background: "rgba(29,158,117,0.08)",
    transition: "background 0.15s", letterSpacing: "0.03em",
    whiteSpace: "nowrap",
    textAlign: "center",
  },
  userRow: { display: "flex", alignItems: "center", gap: 12, width: "100%" },
  userInfo: { display: "flex", flexDirection: "column", gap: 4, alignItems: "flex-start" },
  avatar: { width: 32, height: 32, borderRadius: "50%", border: "1px solid var(--teal-dim)" },
  userName: { fontSize: 13, color: "var(--text-primary)", fontWeight: 500 },
  logoutBtn: {
    fontSize: 11, padding: "2px 8px", borderRadius: 4,
    border: "1px solid rgba(255,255,255,0.12)",
    color: "var(--text-secondary)", cursor: "pointer",
    background: "rgba(255,255,255,0.04)",
  },

  /* ── Mobile View Overlays and Drawers ── */
  backdrop: {
    position: "fixed", inset: 0, zIndex: 298,
    background: "rgba(0,0,0,0.45)",
  },
  drawer: {
    position: "fixed", top: 0, left: 0, bottom: 0,
    width: 280, zIndex: 299,
    background: "rgba(10,15,20,0.97)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRight: "1px solid rgba(255,255,255,0.07)",
    display: "flex", flexDirection: "column",
    transition: "transform 0.28s cubic-bezier(0.4,0,0.2,1)",
    overflowY: "auto",
  },
  drawerHeader: {
    padding: "24px 20px 20px",
    borderBottom: "1px solid rgba(255,255,255,0.06)",
  },
  drawerLogo: { display: "flex", alignItems: "center", gap: 12 },
  drawerLinks: {
    display: "flex", flexDirection: "column",
    padding: "12px 0", flex: 1,
  },
  drawerLink: {
    position: "relative",
    display: "flex", alignItems: "center",
    padding: "13px 24px",
    fontSize: 15, fontWeight: 400,
    color: "rgba(240,237,232,0.65)",
    background: "none", border: "none",
    cursor: "pointer", textAlign: "left",
    transition: "color 0.15s, background 0.15s",
    letterSpacing: "0.01em",
  },
  drawerLinkActive: {
    color: "var(--teal-light)",
    background: "rgba(29,158,117,0.07)",
    fontWeight: 500,
  },
  drawerActivePill: {
    position: "absolute", left: 0, top: "50%",
    transform: "translateY(-50%)",
    width: 3, height: 22,
    background: "var(--teal-light)",
    borderRadius: "0 2px 2px 0",
  },
  drawerPortalBtn: {
    margin: "12px 20px 28px",
    padding: "11px 16px", borderRadius: 8,
    border: "1px solid var(--teal-dim)",
    color: "var(--teal-light)", fontSize: 13, fontWeight: 500,
    cursor: "pointer", background: "rgba(29,158,117,0.08)",
    textAlign: "center",
  },
  drawerUserRow: {
    display: "flex", alignItems: "center", gap: 10,
    padding: "16px 20px 28px",
    borderTop: "1px solid rgba(255,255,255,0.06)",
  },
};